/**
 * Root error boundary — HandiPrêt
 *
 * Catches unhandled errors in the app and displays a branded error page
 * with a retry button.
 */

"use client";

import { useEffect } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";

interface ErrorProps {
    error: Error & { digest?: string };
    reset: () => void;
}

export default function RootError({ error, reset }: ErrorProps) {
    useEffect(() => {
        console.error("[HandiPrêt Error]", error);
    }, [error]);

    return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-hp-purple/5 via-background to-hp-orange/5">
            <div className="glass rounded-2xl p-8 max-w-md w-full text-center space-y-6">
                <div className="flex justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-red-500 to-hp-orange shadow-hp">
                        <AlertTriangle className="h-8 w-8 text-white" aria-hidden="true" />
                    </div>
                </div>

                <div>
                    <h2 className="text-2xl font-black text-hp-gray">
                        Oups, une erreur est survenue
                    </h2>
                    <p className="mt-2 text-hp-gray-light text-sm">
                        Quelque chose ne s&apos;est pas passé comme prévu.
                        Pas d&apos;inquiétude, vous pouvez réessayer.
                    </p>
                </div>

                {error.digest && (
                    <p className="text-xs text-muted-foreground font-mono">
                        Référence : {error.digest}
                    </p>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                    <Button
                        onClick={reset}
                        className="bg-gradient-to-r from-hp-purple to-hp-orange text-white shadow-hp hover:from-hp-purple-dark hover:to-hp-orange-dark rounded-xl"
                        size="lg"
                    >
                        <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                        Réessayer
                    </Button>

                    <Button
                        variant="outline"
                        className="border-hp-purple text-hp-purple hover:bg-hp-purple hover:text-white rounded-xl"
                        size="lg"
                        asChild
                    >
                        <Link href="/">
                            <Home className="mr-2 h-4 w-4" aria-hidden="true" />
                            Accueil
                        </Link>
                    </Button>
                </div>
            </div>
        </div>
    );
}
